import { FunnelStepHeader, LoadingCard } from '@folio/components';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Asset,
  brokerageFirmControllerGetBrokerageFirmQueryOptions,
  CalculatePolicyQuoteOutput,
  configControllerGetPublicProductConfigQueryOptions,
  derogationControllerCheckDerogationRequirementQueryOptions,
  keyValueFieldsToObject,
  legacyPermissionsControllerAtMeQueryOptions,
  policyControllerCalculatePolicyQuote,
  policyControllerCalculatePolicyQuoteQueryKey,
  viewSpecificControllerListPolicyAssetsQueryOptions,
} from 'korint-sdk';
import { FormSections, FormSectionsWithShortcuts } from 'korint-sdk/react';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { PolicyTimeZoneProvider } from '@folio/utils/time-zone';
import {
  isQuoteDerogationApproved,
  shouldDisableQuoteFields,
} from '@folio/utils/quoteDerogationLock';
import { useHasPolicyProductConfig } from '@folio/utils/usePolicyProductConfig';
import { useCurrentActiveAsset } from '@folio/utils/useCurrentActiveAsset';
import { DelegatedQuoteSections } from './DelegatedQuoteSections';
import { NonDelegatedQuoteSections } from './NonDelegatedQuoteSections';
import { WaitingForQuoteCard } from './WaitingForQuoteCard';
import { useAvailablePricing } from '../shared-quote/use-available-pricing';
import { WakamQuoteFunnelError } from '../PolicyWarningBanner/WakamQuoteFunnelError';

export type LegacyQuoteModuleProps = {
  mode?: 'stepper' | 'standalone';
  showPreviousButton?: boolean;
  showQuoteRecap?: 'aside' | 'inline' | false;
  policySections?: FormSectionsWithShortcuts;
  policySectionsAfterOptions?: FormSectionsWithShortcuts;
  assetSections?: FormSectionsWithShortcuts;
  additionalQuoteComponentsSections?: Array<FormSections[number] & { forTypes?: string[] }>;
  manualQuoteSections?: FormSections;
  nonDelegatedDocuments?: string[];
  additionalComponent?: React.ReactNode;
  additionalComponentAfterOptions?: React.ReactNode;
  overrideDelegatedComponents?: React.ReactNode;
  helper?: React.ReactNode;
  title?: string;
  subtitle?: string;
};

export function LegacyQuoteFunnelModule(props: LegacyQuoteModuleProps) {
  return (
    <PolicyTimeZoneProvider>
      <LegacyQuoteFunnel {...props} />
    </PolicyTimeZoneProvider>
  );
}

function LegacyQuoteFunnel({
  mode = 'stepper',
  showPreviousButton = true,
  showQuoteRecap = 'aside',
  policySections = [],
  policySectionsAfterOptions = [],
  assetSections = [],
  manualQuoteSections = [],
  additionalQuoteComponentsSections = [],
  nonDelegatedDocuments = [],
  additionalComponent,
  additionalComponentAfterOptions,
  overrideDelegatedComponents,
  helper,
  title,
  subtitle,
}: LegacyQuoteModuleProps) {
  const { t } = useTranslation();
  const { policy, workingBranch } = usePolicyOnDashboard();
  const isWakamErrorEnabled = useHasPolicyProductConfig('isWakamErrorEnabled');
  const resolvedAdditionalComponent =
    additionalComponent ?? (isWakamErrorEnabled ? <WakamQuoteFunnelError /> : undefined);
  const resolvedAssetSections = assetSections.map((section, si) => ({
    ...section,
    title:
      t(`${policy.productId}.quoteFunnel.assetSections.${si}.title`, {
        defaultValue: section.title ?? '',
      }) || undefined,
  }));
  const resolvedAdditionalQuoteComponentsSections = additionalQuoteComponentsSections.map(
    (section, si) => ({
      ...section,
      title:
        t(`${policy.productId}.quoteFunnel.additionalQuoteComponentsSections.${si}.title`, {
          defaultValue: section.title ?? '',
        }) || undefined,
    }),
  );

  const { data: asset, isLoading: isGetAssetLoading } = useCurrentActiveAsset({
    policyId: policy.id,
    branchId: workingBranch?.id,
  });

  const { data: assets, isLoading: isGetAssetsLoading } = useQuery({
    ...viewSpecificControllerListPolicyAssetsQueryOptions(policy.id, {
      branchId: workingBranch?.id,
    }),
    select: data =>
      data.data
        .filter(asset => asset.status !== 'ASSET_STOPPED')
        .map(asset => ({
          ...asset,
          customFields: keyValueFieldsToObject(asset.customFields ?? []),
        })) as Asset[],
  });
  const assetIds = assets?.map(asset => asset.id) ?? [];

  const { data: quoteConfig, isLoading: isConfigLoading } = useQuery({
    ...configControllerGetPublicProductConfigQueryOptions(policy.productId),
    select: data => data.quote,
  });

  const { data: isQuoteDelegated, isLoading: isQuoteDelegatedLoading } = useQuery({
    queryKey: policyControllerCalculatePolicyQuoteQueryKey(
      policy.id,
      {
        assetIds,
        options: assetIds.map(assetId => ({
          assetId,
          ...quoteConfig?.defaultConfig!,
        })),
      },
      { branchId: workingBranch?.id! },
    ),
    queryFn: async () => {
      try {
        const quote: CalculatePolicyQuoteOutput = await policyControllerCalculatePolicyQuote(
          policy.id,
          {
            assetIds,
            options: assetIds.map(assetId => ({
              assetId,
              ...quoteConfig?.defaultConfig!,
            })),
          },
          { branchId: workingBranch?.id! },
        );

        return Boolean(quote.assets) && Boolean(quote.policy);
      } catch (error) {
        const quoteErrors = (error as any)?.errorResponse?.data?.details?.quoteErrors as
          | { key: string; error: string }[]
          | undefined;
        if (quoteErrors?.some(e => e.key === 'eligibility' && e.error?.includes('CFDP STUDY'))) {
          return false;
        }
        console.warn('Quote calculation failed, falling back to delegated mode', error);
        return true;
      }
    },
    retry: false,
    enabled: Boolean(asset) && Boolean(quoteConfig) && Boolean(workingBranch),
  });

  const { data: needsDerogation, isLoading: isNeedsDerogationLoading } = useQuery({
    ...derogationControllerCheckDerogationRequirementQueryOptions(
      workingBranch?.id!,
      {},
      {
        action: 'QUOTE',
      },
    ),
    select: data => data.needsDerogation,
    enabled: Boolean(workingBranch?.id),
  });

  const { data: user, isLoading: isAtMeLoading } = useQuery({
    ...legacyPermissionsControllerAtMeQueryOptions(),
  });

  const { data: firmType, isLoading: isGetBrokerageFirmLoading } = useQuery({
    ...brokerageFirmControllerGetBrokerageFirmQueryOptions(user?.brokerageFirmId!),
    select: data => data.firmType,
    enabled: Boolean(user?.brokerageFirmId),
  });

  const { isLoading: isAvailablePricingLoading } = useAvailablePricing(policy.id, {
    branchId: workingBranch?.id,
    enabled: Boolean(policy.id) && Boolean(workingBranch?.id),
  });

  const areQueriesLoading =
    isGetAssetLoading ||
    isGetAssetsLoading ||
    isConfigLoading ||
    isQuoteDelegatedLoading ||
    isAtMeLoading ||
    isGetBrokerageFirmLoading ||
    isNeedsDerogationLoading ||
    isAvailablePricingLoading;

  if (areQueriesLoading) {
    return <LoadingCard />;
  }

  if (!workingBranch || !asset || !quoteConfig) {
    return null;
  }

  const showWaitingForQuote =
    needsDerogation &&
    !isQuoteDerogationApproved(workingBranch) &&
    policy.status !== 'POLICY_QUOTED';

  const disableQuoteFields = shouldDisableQuoteFields(workingBranch, firmType);

  const header = (
    <FunnelStepHeader
      title={title ?? t('funnelSteps.quote.title')}
      subtitle={subtitle ?? t('funnelSteps.quote.subtitle', '')}
    />
  );

  if (showWaitingForQuote) {
    return (
      <div className="flex flex-col gap-6">
        {header}
        <WaitingForQuoteCard
          policy={policy}
          asset={asset}
          assets={assets || []}
          branch={workingBranch}
          quoteConfig={quoteConfig}
          policySections={policySections}
          assetSections={resolvedAssetSections}
        />
      </div>
    );
  }

  const quoteSections = isQuoteDelegated ? (
    <DelegatedQuoteSections
      mode={mode}
      showPreviousButton={showPreviousButton}
      showQuoteRecap={showQuoteRecap}
      firmType={firmType}
      policy={policy}
      branch={workingBranch}
      asset={asset}
      assets={assets || []}
      quoteConfig={quoteConfig}
      additionalQuoteComponentsSections={resolvedAdditionalQuoteComponentsSections}
      policySections={policySections}
      policySectionsAfterOptions={policySectionsAfterOptions}
      assetSections={resolvedAssetSections}
      additionalComponent={resolvedAdditionalComponent}
      additionalComponentAfterOptions={additionalComponentAfterOptions}
      overrideDelegatedComponents={overrideDelegatedComponents}
      helper={helper}
      disabled={disableQuoteFields}
    />
  ) : (
    <NonDelegatedQuoteSections
      mode={mode}
      showPreviousButton={showPreviousButton}
      showQuoteRecap={showQuoteRecap}
      firmType={firmType}
      policy={policy}
      branch={workingBranch}
      asset={asset}
      assets={assets || []}
      quoteConfig={quoteConfig}
      additionalQuoteComponentsSections={resolvedAdditionalQuoteComponentsSections}
      policySections={policySections}
      assetSections={resolvedAssetSections}
      manualQuoteSections={manualQuoteSections}
      nonDelegatedDocuments={nonDelegatedDocuments}
      additionalComponent={resolvedAdditionalComponent}
      helper={helper}
      disabled={disableQuoteFields}
    />
  );

  return (
    <div className="flex flex-col gap-6">
      {header}
      {quoteSections}
    </div>
  );
}
